// La carta de UNA autoridad dentro del trámite (CC-02, bloque B11; `ui/MG-ILE-1075.md`).
//
// Es lo que hasta el Sprint 3 era el cuerpo entero de la pantalla de la carta (HU-20, HU-21,
// HU-22): preparar, firmar y enviar o solicitar la firma del PRFV, presentar, el cargo y las
// versiones. Con varias autoridades destino (RN-77) el trámite tiene una carta por autoridad y
// cada una vive en su contenedor `carta-de-autoridad` (`data-autoridad`), con su estado, su firma,
// su envío, su cargo y su plazo (RN-80). Los testid de las piezas no llevan la autoridad: se
// buscan DENTRO del contenedor (`MG-ILE-46.md`).
//
// CON UNA SOLA CARTA LA PANTALLA ES LA DE HOY (AT §11.6): el contenedor no tiene título ni fila de
// estado, el aviso y el error van arriba como antes y las llamadas son las mismas. Con varias,
// el contenedor se titula «Carta a {autoridad}», muestra su estado, su plazo y los avisos de
// préstamo (RN-81), y el aviso o el error de un acto se leen dentro de la carta que lo provocó.
//
// LAS ACCIONES SON LAS QUE EL SERVIDOR DICE PARA ESTA CARTA (`acciones_de_la_carta`): la ruta de
// firma la decide su autoridad (RN-78), no la pantalla.
//
// CC-01 (`ui/MG-ILE-46.md`, `ui/MG-ILE-1073.md`; RN-68, RN-69): antes de firmarla, «Editar texto»
// y «Adjuntar carta final» abren su panel dentro de «Preparar la carta» de ESTA carta (solo si el
// servidor trae `editar` / `adjuntar`). Cada versión dice su origen, quién, cuándo y por qué; la
// adjunta se descarga en vez de «Ver texto». Con la firma pedida, guardar pide confirmación porque
// anula la solicitud (`texto_del_efecto_en_la_firma`). El selector del cargo usa el tope del
// servidor (`adjunto_de_la_carta`, decisión 11), no un número escrito aquí.

import { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FileCheck2, FilePen, FileText, FileUp, History, Hourglass, Lock, PenLine, RefreshCw, Send } from 'lucide-react';
import { ACEPTA, Alert, Button, Card, Field, Input, Mono, StatusBadge, SubidaDeArchivo } from './ui';
import {
  ConfirmarAnularLaFirma, ConfirmarDescartar, MENSAJES, PanelDeAdjunto, PanelDeEdicion, errorDelArchivo,
} from './EdicionDeLaCarta';
import type { ErroresDeLaVersion } from './EdicionDeLaCarta';
import EstadoDelAcuse from './EstadoDelAcuse';
import RelojDelPlazo from './RelojDelPlazo';
import { comoFechaDeInstante, comoFechaYHora, fechaDeHoyEnZona } from '../lib/fechas';
import { MARCAS_DE_AUTORIDAD, tonoDeLaCarta } from '../lib/etiquetas';
import { MENSAJE_CARTA_DESACTUALIZADA, etiquetaDeLaCarta } from '../api/regulatorio';
import type { Tramite, VersionDeLaCarta } from '../api/regulatorio';
import { accionesDeLaCarta, cartasActivas, imprimirFormatoAmarilloParaLaAutoridad } from '../api/cartas';
import type { VistaDeCarta } from '../api/cartas';
import { archivoABase64 } from '../api/plantillas';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { descargarBlob } from '../lib/entradas';
import { abrirDocumentoEnPestana } from '../lib/abrirDocumento';
import BotonVerEImprimir from './BotonVerEImprimir';
import { useZonaDelPais } from '../lib/CatalogosContexto';


/** Qué dice la tarjeta «Preparar la carta»: con qué plantilla se hizo, o qué falta para hacerla (C2/C4). */
const subtituloDePreparar = (carta: VistaDeCarta, amarilloCompleto: boolean): string => {
  if (carta.plantilla_usada) {
    return `Plantilla usada: ${carta.plantilla_usada}. La carta conserva el texto de la versión con la que se generó.`;
  }
  // CC-01: la vigente es una carta adjunta, que no sale de ninguna plantilla.
  if (carta.carta_version > 0) {
    return 'La versión vigente es la carta final adjunta en PDF; las anteriores conservan su texto.';
  }
  if (amarilloCompleto) {
    return 'Se prepara con la plantilla vigente de la autoridad del país y los datos del caso.';
  }
  return 'Completa el Formato Amarillo antes de preparar la carta.';
};

/** HU-21 · la ruta de firma la decide la autoridad (RN-05, RN-78): con PRFV o con la firma pre-configurada (HU-40). */
const subtituloDeFirma = (carta: VistaDeCarta): string => {
  const autoridad = carta.autoridad_nombre ?? 'La autoridad';
  if (carta.exige_profesional_responsable) {
    return `${autoridad} exige la firma del Profesional Responsable de Farmacovigilancia: se solicita, se espera y presenta el Representante del País.`;
  }
  const firmante = carta.firmante_responsable_configurado ? ` de ${carta.firmante_responsable_configurado}` : '';
  return `${autoridad} no exige PRFV: Asuntos Regulatorios firma con la firma pre-configurada${firmante} y envía en la misma acción.`;
};

/** HU-20-C5: con qué versión del Formato Amarillo se generó la carta vigente (`null` sin carta). */
const amarilloDeLaVigente = (carta: VistaDeCarta): number | null =>
  carta.versiones_de_la_carta.find((v) => v.es_la_vigente)?.formato_amarillo_version ?? null;

/** La misma carta en la respuesta de un acto: por su autoridad o, sin `cartas[]`, la raíz. */
const laMismaCarta = (t: Tramite, codigo: string | null): VistaDeCarta | undefined =>
  cartasActivas(t).find((c) => c.autoridad_codigo === codigo) ?? (codigo === null ? cartasActivas(t)[0] : undefined);

function Version({ v, plantilla, zonaHoraria, onDescargar }: Readonly<{
  v: VersionDeLaCarta; plantilla: string | null;
  /** RN-82: la zona del país del caso, en la que se lee la hora de la versión; `null` = Lima. */
  zonaHoraria: string | null;
  /** CC-01-C2: descargar el PDF de una versión adjunta. */
  onDescargar: (v: VersionDeLaCarta) => void;
}>) {
  // CC-01: lo que se puede hacer con la fila lo dice el servidor; sin el campo (servidor
  // anterior), «Ver texto» como siempre.
  const permitidas = v.acciones_permitidas ?? ['ver_texto'];
  const verTexto = permitidas.includes('ver_texto');
  const [abierta, setAbierta] = useState(v.es_la_vigente && verTexto);
  // CC-01: al nacer una versión nueva, la que deja de ser vigente se pliega y la nueva se abre.
  useEffect(() => { setAbierta(v.es_la_vigente && verTexto); }, [v.es_la_vigente, verTexto]);
  return (
    <li className="py-2 text-meta" data-testid="carta-version">
      <div className="flex flex-wrap items-center gap-2">
        <Mono className="font-semibold">Carta v{v.version}</Mono>
        {v.es_la_vigente && <StatusBadge tone="success" icon={CheckCircle2}>Vigente</StatusBadge>}
        {/* CC-01: el origen con texto (no solo color) y quién, cuándo y de dónde, del servidor. */}
        {v.etiqueta_del_origen && <StatusBadge tone="info" data-testid="carta-version-origen">{v.etiqueta_del_origen}</StatusBadge>}
        {v.texto_del_origen
          ? <span className="text-ink-soft" data-testid="carta-version-autor">{v.texto_del_origen}</span>
          : (
            <>
              {plantilla && <StatusBadge tone="info">{plantilla.replace(/v\d+$/, `v${v.plantilla_version}`)}</StatusBadge>}
              <span className="text-ink-soft">Formato Amarillo v{v.formato_amarillo_version}</span>
            </>
          )}
        {/* RN-81: el préstamo se ve también en la fila de la versión que lo usó. */}
        {v.texto_de_plantilla_prestada && (
          <StatusBadge tone="warning" icon={AlertTriangle} data-testid="carta-version-plantilla-prestada">{v.texto_de_plantilla_prestada}</StatusBadge>
        )}
        {(v.origen ?? 'generada') === 'generada' && <span className="text-ink-soft">{comoFechaYHora(v.generada_en, zonaHoraria)}</span>}
        {!v.lista_para_firmar && (
          <StatusBadge tone="warning" icon={AlertTriangle}>Pendiente: {v.campos_pendientes.join(', ')}</StatusBadge>
        )}
        {verTexto && (
          <Button size="sm" variant="ghost" onClick={() => setAbierta((a) => !a)} data-testid="carta-version-ver-texto">
            {abierta ? 'Ocultar texto' : 'Ver texto'}
          </Button>
        )}
        {permitidas.includes('descargar') && (
          <Button size="sm" variant="secondary" icon={Download} onClick={() => onDescargar(v)} data-testid="carta-version-descargar"
            aria-label={`Descargar PDF de la carta v${v.version}`}>
            Descargar PDF
          </Button>
        )}
      </div>
      {v.motivo && <p className="mt-1 text-ink-soft" data-testid="carta-version-motivo">Motivo: {v.motivo}</p>}
      {v.archivo && (
        <p className="mt-1 text-ink-soft">
          Huella SHA-256: <span data-testid="carta-version-huella" title={v.archivo.huella_sha256}><Mono>{v.archivo.huella_sha256.slice(0, 16)}…</Mono></span>
        </p>
      )}
      {verTexto && abierta && (
        <pre className="mt-2 whitespace-pre-wrap rounded-xl border border-hairline bg-surface p-3 font-sans text-meta" aria-label={`Texto de la carta v${v.version}`}>
          {v.texto}
        </pre>
      )}
    </li>
  );
}

/** Con varias cartas: «Carta a SANIPES», su estado, «Por confirmar con ILENDER» y su plazo. */
function TituloDeLaCarta({ carta, idDelTitulo, zonaHoraria }: Readonly<{ carta: VistaDeCarta; idDelTitulo: string; zonaHoraria: string | null }>) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-3">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <h2 id={idDelTitulo} className="text-titulo font-semibold text-ink" data-testid="carta-titulo">
          Carta a {carta.autoridad_nombre}
        </h2>
        {carta.texto_de_la_carta && (
          <StatusBadge tone={tonoDeLaCarta(carta.estado_carta)} data-testid="carta-estado-insignia">{carta.texto_de_la_carta}</StatusBadge>
        )}
        {carta.carta?.por_confirmar && (
          <StatusBadge tone="warning" icon={AlertTriangle} data-testid="carta-por-confirmar">{MARCAS_DE_AUTORIDAD.por_confirmar}</StatusBadge>
        )}
      </div>
      {carta.carta?.notificacion && <RelojDelPlazo etiqueta="Notificación" reloj={carta.carta.notificacion} zonaHoraria={zonaHoraria} data-testid="carta-plazo" />}
    </div>
  );
}

/** El título del aviso de la carta cuando no se pudo abrir el documento. */
const TITULO_FALLO_AL_IMPRIMIR = 'No se pudo abrir el Formato Amarillo.';

/**
 * «Ver e imprimir» del Formato Amarillo con el encabezado de esta autoridad (CC2-19-C1).
 *
 * Botón y no enlace (BUG-1090): el documento se pide con el token y se abre en otra pestaña con
 * `abrirDocumentoEnPestana`. El error no se pinta aquí: sube por `onFallo` al aviso de la carta,
 * que es donde se leen los errores de sus actos (`null` lo limpia al volver a intentarlo).
 */
function ImprimirParaLaAutoridad({ casoId, version, carta, onFallo }: Readonly<{
  casoId: string;
  version: number;
  carta: VistaDeCarta;
  onFallo: (fallo: Fallo | null) => void;
}>) {
  const [abriendo, setAbriendo] = useState(false);
  const codigo = carta.autoridad_codigo;
  if (!codigo || version < 1) return null;

  // Sin `await` antes de `abrirDocumentoEnPestana`: la pestaña tiene que abrirse en el clic.
  const imprimir = async () => {
    if (abriendo) return;
    onFallo(null);
    try {
      await abrirDocumentoEnPestana(() => {
        setAbriendo(true);
        return imprimirFormatoAmarilloParaLaAutoridad(casoId, version, codigo);
      });
    } catch (error) {
      onFallo(aFallo(error));
    } finally {
      setAbriendo(false);
    }
  };

  return (
    <BotonVerEImprimir ocupado={abriendo} texto="Ver e imprimir" onImprimir={() => { void imprimir(); }}
      aria-label={`Ver e imprimir el Formato Amarillo v${version} para ${carta.autoridad_nombre}`}
      data-testid="carta-imprimir-formato" data-autoridad={codigo} data-version={version} />
  );
}

export default function CartaDeAutoridad({ casoId, tramite, carta, varias, actor, onTramite }: Readonly<{
  casoId: string;
  /** El trámite: el Formato Amarillo es uno solo para todas las cartas (RN-77). */
  tramite: Tramite;
  carta: VistaDeCarta;
  /** El trámite tiene más de una carta activa: el contenedor lleva título, estado y plazo. */
  varias: boolean;
  actor?: string;
  /** El trámite que devolvió el servidor tras un acto (§8.1 R3). */
  onTramite: (t: Tramite) => void;
}>) {
  const idDelTitulo = useId();
  // RN-82: las fechas de la carta, en la zona del país del caso.
  const zona = useZonaDelPais()(tramite.pais);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  // El título del error dice QUÉ no se pudo: «No se pudo firmar y enviar» y no siempre
  // «No se pudo preparar la carta», que era mentira en cinco de las seis acciones.
  const [tituloDelFallo, setTituloDelFallo] = useState('No se pudo completar');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // HU-21: quién firmó / quién presenta y cuándo.
  const [firmante, setFirmante] = useState('');
  const [fechaFirma, setFechaFirma] = useState(() => fechaDeHoyEnZona(zona));
  const [presentador, setPresentador] = useState(actor ?? '');
  const [fechaPresentacion, setFechaPresentacion] = useState(() => fechaDeHoyEnZona(zona));
  // HU-22: el cargo.
  const [referencia, setReferencia] = useState('');
  const [fechaCargo, setFechaCargo] = useState(() => fechaDeHoyEnZona(zona));
  const [archivoCargo, setArchivoCargo] = useState<File | null>(null);
  // CC-01: el panel abierto (uno a la vez), lo escrito y los errores por campo.
  const [panel, setPanel] = useState<'edicion' | 'adjunto' | null>(null);
  const [textoEditado, setTextoEditado] = useState('');
  const [motivo, setMotivo] = useState('');
  const [archivoFinal, setArchivoFinal] = useState<File | null>(null);
  const [errores, setErrores] = useState<ErroresDeLaVersion>({});
  const [pendiente, setPendiente] = useState<(() => Promise<void>) | null>(null);
  const [descartando, setDescartando] = useState(false);
  const raiz = useRef<HTMLDivElement | null>(null);

  const actos = accionesDeLaCarta(casoId, carta.autoridad_codigo);

  /** Ejecuta un paso de la ruta (HU-20/HU-21/HU-22) de ESTA carta y refresca el trámite. */
  const paso = async (titulo: string, accion: () => Promise<Tramite>, mensaje: (c: VistaDeCarta, t: Tramite) => string): Promise<boolean> => {
    setFallo(null);
    setAviso(null);
    setTituloDelFallo(titulo);
    setOcupado(true);
    try {
      const t = await accion();
      onTramite(t);
      setAviso(mensaje(laMismaCarta(t, carta.autoridad_codigo) ?? carta, t));
      return true;
    } catch (e) {
      setFallo(aFallo(e));
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const preparar = (regenerar: boolean) => paso(
    regenerar ? 'No se pudo regenerar la carta' : 'No se pudo preparar la carta',
    () => actos.preparar(regenerar),
    (c) => `Carta v${c.carta_version} preparada con la plantilla ${c.plantilla_usada ?? ''}.`.trim(),
  );

  const subirCargo = async () => {
    setTituloDelFallo('No se pudo registrar el cargo');
    if (!archivoCargo) { setFallo({ message: 'Elige el archivo del cargo que devolvió la autoridad.' }); return; }
    if (!referencia.trim()) { setFallo({ message: 'Indica la referencia del cargo (número o sello de la autoridad).' }); return; }
    const archivo = archivoCargo;
    const bien = await paso(
      'No se pudo registrar el cargo',
      async () => actos.registrarCargo({
        referencia_autoridad: referencia.trim(), fecha_recepcion: fechaCargo, nombre_archivo: archivo.name,
        contenido_base64: await archivoABase64(archivo), tipo_mime: archivo.type || null, registrado_por: actor ?? null,
      }),
      (c) => `${c.cargo?.etiqueta ?? 'Cargo'} registrado: la notificación queda concluida.`,
    );
    // El archivo elegido se suelta SOLO si se registró: si falló, el usuario corrige la
    // referencia y vuelve a intentar sin tener que buscar el PDF otra vez.
    if (bien) setArchivoCargo(null);
  };

  const bajarCargo = async () => {
    setFallo(null);
    setTituloDelFallo('No se pudo descargar el cargo');
    try {
      descargarBlob(await actos.descargarCargo(), carta.cargo?.nombre_archivo ?? 'cargo');
    } catch (e) {
      setFallo(aFallo(e));
    }
  };

  const acciones = new Set(carta.acciones_de_la_carta);
  const amarilloCompleto = tramite.estado_formato_amarillo === 'completo';
  const testidDelFallo = fallo?.estado === 403 ? 'carta-sin-permiso' : 'carta-error-envio';
  // Decisión 11: el tope único de la carta final y del cargo, del servidor.
  const limites = tramite.adjunto_de_la_carta ?? null;
  const vigente = carta.versiones_de_la_carta.find((v) => v.es_la_vigente);

  // ---- CC-01 · editar o adjuntar -------------------------------------------------------------

  /** Devuelve el foco al botón que abrió el panel (spec, «Accesibilidad»). */
  const enfocar = (testid: string) => {
    setTimeout(() => raiz.current?.querySelector<HTMLElement>(`[data-testid="${testid}"]`)?.focus(), 0);
  };
  const abrir = (cual: 'edicion' | 'adjunto') => {
    setFallo(null);
    setAviso(null);
    setErrores({});
    setMotivo('');
    setArchivoFinal(null);
    setTextoEditado(vigente?.texto ?? '');
    setPanel(cual);
  };
  const cerrar = () => {
    const abierto = panel;
    setPanel(null);
    setDescartando(false);
    setErrores({});
    enfocar(abierto === 'adjunto' ? 'carta-adjuntar' : 'carta-editar');
  };
  const hayCambios = panel === 'edicion'
    ? textoEditado !== (vigente?.texto ?? '') || motivo.trim() !== ''
    : motivo.trim() !== '' || archivoFinal !== null;
  const cancelar = () => {
    if (panel === 'edicion' && hayCambios) { setDescartando(true); return; }
    cerrar();
  };
  const salirDe = (campo: 'texto' | 'motivo') => {
    const valor = campo === 'texto' ? textoEditado : motivo;
    if (!valor.trim()) setErrores((e) => ({ ...e, [campo]: campo === 'texto' ? MENSAJES.textoVacio : MENSAJES.sinMotivo }));
  };

  /** Guarda la versión (o pide confirmar si anula la firma). Un 422 con `campo` va bajo su campo. */
  const guardar = (titulo: string, accion: () => Promise<Tramite>, archivoParaReintentar: File | null) => {
    const hacer = async () => {
      setFallo(null);
      setAviso(null);
      setTituloDelFallo(titulo);
      setOcupado(true);
      try {
        const t = await accion();
        onTramite(t);
        const c = laMismaCarta(t, carta.autoridad_codigo) ?? carta;
        const nueva = c.versiones_de_la_carta.find((v) => v.es_la_vigente);
        setAviso(`Carta v${c.carta_version} guardada${nueva?.texto_del_origen ? `: ${nueva.texto_del_origen}` : '.'}`);
        setPanel(null);
        setArchivoFinal(null);
        setErrores({});
      } catch (e) {
        const f = aFallo(e);
        const campo = f.cuerpo?.campo;
        if (f.estado === 422 && (campo === 'texto' || campo === 'motivo' || campo === 'archivo')) {
          setErrores((previos) => ({ ...previos, [campo]: f.message }));
        } else {
          setFallo(f);
        }
        // Si falla, el archivo elegido se conserva para reintentar (como el cargo).
        if (archivoParaReintentar) setArchivoFinal(archivoParaReintentar);
      } finally {
        setOcupado(false);
        setPendiente(null);
      }
    };
    if (carta.texto_del_efecto_en_la_firma) {
      setPendiente(() => hacer);
      return;
    }
    hacer();
  };

  const guardarEdicion = () => {
    const faltan: ErroresDeLaVersion = {
      texto: textoEditado.trim() ? undefined : MENSAJES.textoVacio,
      motivo: motivo.trim() ? undefined : MENSAJES.sinMotivo,
    };
    setErrores(faltan);
    if (faltan.texto || faltan.motivo) return;
    guardar('No se pudo guardar la edición', () => actos.editar({ texto: textoEditado, motivo: motivo.trim(), version_base: carta.carta_version }), null);
  };

  const guardarAdjunto = () => {
    const faltan: ErroresDeLaVersion = {
      archivo: errorDelArchivo(archivoFinal, limites?.tamano_maximo_mb),
      motivo: motivo.trim() ? undefined : MENSAJES.sinMotivo,
    };
    setErrores(faltan);
    if (faltan.archivo || faltan.motivo || !archivoFinal) return;
    const archivo = archivoFinal;
    guardar('No se pudo adjuntar la carta', async () => actos.adjuntar({
      nombre_archivo: archivo.name, contenido_base64: await archivoABase64(archivo), tipo_mime: archivo.type || null,
      motivo: motivo.trim(), version_base: carta.carta_version,
    }), archivo);
  };

  const elegirArchivoFinal = (archivo: File | null) => {
    setArchivoFinal(archivo);
    setErrores((e) => ({ ...e, archivo: archivo ? errorDelArchivo(archivo, limites?.tamano_maximo_mb) : undefined }));
  };

  const alFallarLaImpresion = (f: Fallo | null) => {
    setTituloDelFallo(TITULO_FALLO_AL_IMPRIMIR);
    setFallo(f);
  };

  const bajarVersion = async (v: VersionDeLaCarta) => {
    setFallo(null);
    setTituloDelFallo('No se pudo descargar la carta');
    try {
      descargarBlob(await actos.descargarVersion(v.version), v.archivo?.nombre ?? `carta_v${v.version}.pdf`);
    } catch (e) {
      setFallo(aFallo(e));
    }
  };
  const conPanel = panel !== null;

  const contenido = (
    <>
      {varias && <TituloDeLaCarta carta={carta} idDelTitulo={idDelTitulo} zonaHoraria={zona} />}
      {fallo && (
        <div className="mb-3">
          <Alert tone={fallo.estado === 422 || fallo.estado === 409 ? 'warning' : 'danger'} title={tituloDelFallo} detalleTecnico={fallo.detalleTecnico}
            onCerrar={() => setFallo(null)} data-testid={testidDelFallo}>
            {fallo.message}
          </Alert>
        </div>
      )}
      {aviso && <div className="mb-3"><Alert tone="success" title={aviso} onCerrar={() => setAviso(null)} data-testid="carta-exito" /></div>}
      {/* RN-81: lo que esta carta toma de la predeterminada, dicho por el servidor. */}
      {carta.carta?.texto_de_plazo_prestado && (
        <div className="mb-3"><Alert tone="warning" icon={AlertTriangle} data-testid="carta-aviso-plazo-prestado">{carta.carta.texto_de_plazo_prestado}</Alert></div>
      )}
      {carta.carta?.texto_de_dias_de_aviso_prestados && (
        <div className="mb-3"><Alert tone="warning" icon={AlertTriangle} data-testid="carta-aviso-dias-prestados">{carta.carta.texto_de_dias_de_aviso_prestados}</Alert></div>
      )}
      {carta.carta?.texto_de_plantilla_prestada && (
        <div className="mb-3"><Alert tone="warning" icon={AlertTriangle} data-testid="carta-aviso-plantilla-prestada">{carta.carta.texto_de_plantilla_prestada}</Alert></div>
      )}
      {carta.carta_desactualizada && (
        <div className="mb-3">
          <Alert tone="warning" title={MENSAJE_CARTA_DESACTUALIZADA} icon={AlertTriangle}>
            La carta v{carta.carta_version} se generó con el Formato Amarillo v{amarilloDeLaVigente(carta) ?? '—'}; el trámite ya va por el Formato Amarillo v{tramite.formato_amarillo_version ?? ''}. Regenera la carta para incorporar la corrección.
          </Alert>
        </div>
      )}

      <Card title="Preparar la carta" icon={FileText}
        subtitle={subtituloDePreparar(carta, amarilloCompleto)}
        actions={(
          <span className="inline-flex flex-wrap gap-2">
            {acciones.has('preparar') && (
              <Button size="sm" icon={FileText} onClick={() => { preparar(false); }} disabled={ocupado || !amarilloCompleto || conPanel} data-testid="carta-preparar">Preparar carta</Button>
            )}
            {/* CC-01 (RN-68): solo si el servidor lo ofrece para ESTA carta. */}
            {acciones.has('editar') && (
              <Button size="sm" variant="secondary" icon={FilePen} onClick={() => abrir('edicion')} disabled={ocupado || conPanel} data-testid="carta-editar">
                Editar texto
              </Button>
            )}
            {acciones.has('adjuntar') && (
              <Button size="sm" variant="secondary" icon={FileUp} onClick={() => abrir('adjunto')} disabled={ocupado || conPanel} data-testid="carta-adjuntar">
                Adjuntar carta final
              </Button>
            )}
            {acciones.has('regenerar') && (
              <Button size="sm" variant={carta.carta_desactualizada ? 'primary' : 'secondary'} icon={RefreshCw} onClick={() => { preparar(true); }} disabled={ocupado || conPanel}
                data-testid="carta-regenerar">
                Regenerar carta (v{carta.carta_version + 1})
              </Button>
            )}
          </span>
        )}>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-meta">
          <dt className="text-ink-soft">Formato Amarillo</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {amarilloCompleto ? `Completo · v${tramite.formato_amarillo_version ?? ''}` : 'Pendiente'}
            {varias && amarilloCompleto && (
              <ImprimirParaLaAutoridad casoId={casoId} version={tramite.formato_amarillo_version} carta={carta} onFallo={alFallarLaImpresion} />
            )}
          </dd>
          <dt className="text-ink-soft">Estado de la carta</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {/* V-08 de `MG-ILE-46.md`: el texto del servidor, que dice también el origen. */}
            <span data-testid="carta-estado">{carta.texto_de_la_carta ?? etiquetaDeLaCarta(carta.estado_carta)}</span>
            {carta.estado_carta === 'enviada' && (
              <StatusBadge tone="neutral" icon={Lock} data-testid="carta-edicion-bloqueada">
                {carta.razon_de_edicion_bloqueada ?? 'La carta enviada no se modifica'}
              </StatusBadge>
            )}
          </dd>
        </dl>
        {/* CC-01-C4 / C10: por qué esta carta ya no se edita (firmada o desactualizada). */}
        {carta.razon_de_edicion_bloqueada && carta.estado_carta !== 'enviada' && (
          <div className="mt-3">
            <Alert tone="info" icon={Lock} data-testid="carta-edicion-bloqueada">{carta.razon_de_edicion_bloqueada}</Alert>
          </div>
        )}
        {panel === 'edicion' && (
          <PanelDeEdicion version={carta.carta_version} texto={textoEditado} motivo={motivo} errores={errores} ocupado={ocupado}
            onTexto={(v) => { setTextoEditado(v); setErrores((e) => ({ ...e, texto: undefined })); }}
            onMotivo={(v) => { setMotivo(v); setErrores((e) => ({ ...e, motivo: undefined })); }}
            onSalirDe={salirDe} onGuardar={guardarEdicion} onCancelar={cancelar} />
        )}
        {panel === 'adjunto' && (
          <PanelDeAdjunto version={carta.carta_version} archivo={archivoFinal} motivo={motivo} errores={errores} ocupado={ocupado}
            topeMb={limites?.tamano_maximo_mb} textoDeLimites={limites?.texto_de_limites}
            onArchivo={elegirArchivoFinal}
            onMotivo={(v) => { setMotivo(v); setErrores((e) => ({ ...e, motivo: undefined })); }}
            onSalirDe={salirDe} onGuardar={guardarAdjunto} onCancelar={cancelar} />
        )}
      </Card>
      <ConfirmarAnularLaFirma efecto={pendiente ? carta.texto_del_efecto_en_la_firma ?? null : null} ocupado={ocupado}
        onConfirmar={() => { pendiente?.(); }} onVolver={() => setPendiente(null)} />
      <ConfirmarDescartar abierto={descartando} onDescartar={cerrar} onSeguir={() => setDescartando(false)} />

      {/* HU-21: la ruta de firma y envío, con las acciones que el servidor dice que caben. */}
      {carta.estado_carta !== 'sin_preparar' && (
        <Card className="mt-4" title="Firma y envío a la autoridad" icon={PenLine} subtitle={subtituloDeFirma(carta)}>
          {carta.texto_de_la_espera && (
            <div className="mb-3">
              <Alert tone="warning" title={carta.texto_de_la_espera} icon={Hourglass} data-testid="carta-espera">
                El plazo regulatorio sigue corriendo mientras se espera la firma.
              </Alert>
            </div>
          )}
          {/* HU-45-C5 (campos pendientes) y HU-20-C5 (desactualizada): por qué no se ofrece la firma. */}
          {carta.razon_de_firma_bloqueada && (
            <div className="mb-3">
              <Alert tone="warning" title="La firma no está disponible" icon={AlertTriangle}>{carta.razon_de_firma_bloqueada}</Alert>
            </div>
          )}
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-meta mb-3">
            <dt className="text-ink-soft">Firmante</dt><dd data-testid="carta-firmante">{carta.firmante ?? '—'}</dd>
            <dt className="text-ink-soft">Presentó</dt><dd data-testid="carta-presento">{carta.presentado_por ?? '—'}{carta.fecha_presentacion ? ` · ${comoFechaDeInstante(carta.fecha_presentacion, zona)}` : ''}</dd>
          </dl>
          {acciones.has('firmar_y_enviar') && (
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Presentado por" required>
                <Input aria-label="Presentado por" value={presentador} onChange={(e) => setPresentador(e.target.value)} data-testid="carta-form-presentado-por" />
              </Field>
              <Field label="Fecha de presentación" required>
                <Input aria-label="Fecha de presentación" type="date" value={fechaPresentacion} onChange={(e) => setFechaPresentacion(e.target.value)} data-testid="carta-form-fecha-presentacion" />
              </Field>
              <Button icon={Send} disabled={ocupado || !presentador.trim()} data-testid="carta-firmar-y-enviar"
                onClick={() => { paso('No se pudo firmar y enviar', () => actos.firmarYEnviar(presentador.trim(), fechaPresentacion), (c) => `Carta firmada por ${c.firmante ?? 'el responsable'} y enviada a ${c.autoridad_nombre ?? 'la autoridad'}.`); }}>
                Firmar y enviar
              </Button>
            </div>
          )}
          {acciones.has('solicitar_firma') && (
            <Button icon={PenLine} disabled={ocupado} data-testid="carta-solicitar-firma"
              onClick={() => { paso('No se pudo solicitar la firma', () => actos.solicitarFirma(), (c) => c.texto_de_la_espera ?? 'Firma solicitada al PRFV.'); }}>
              Solicitar firma del PRFV
            </Button>
          )}
          {acciones.has('registrar_firma') && (
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Firmó" required hint="Nombre del PRFV que firmó.">
                <Input aria-label="Firmó" value={firmante} onChange={(e) => setFirmante(e.target.value)} placeholder="Dr. A. Rojas (PRFV)" data-testid="carta-form-firmo" />
              </Field>
              <Field label="Fecha de firma" required>
                <Input aria-label="Fecha de firma" type="date" value={fechaFirma} onChange={(e) => setFechaFirma(e.target.value)} data-testid="carta-form-fecha-firma" />
              </Field>
              <Button icon={CheckCircle2} disabled={ocupado || !firmante.trim()} data-testid="carta-registrar-firma"
                onClick={() => { paso('No se pudo registrar la firma', () => actos.registrarFirma(firmante.trim(), fechaFirma), (c) => `Firmó: ${c.firmante} · ${comoFechaDeInstante(fechaFirma, zona)}. La carta está firmada.`); }}>
                Registrar firma
              </Button>
            </div>
          )}
          {acciones.has('presentar') && (
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Presentado por" required hint="Fuera de Perú, el Representante del País: otra persona que el firmante.">
                <Input aria-label="Presentado por" value={presentador} onChange={(e) => setPresentador(e.target.value)} data-testid="carta-form-presentado-por" />
              </Field>
              <Field label="Fecha de presentación" required>
                <Input aria-label="Fecha de presentación" type="date" value={fechaPresentacion} onChange={(e) => setFechaPresentacion(e.target.value)} data-testid="carta-form-fecha-presentacion" />
              </Field>
              <Button icon={Send} disabled={ocupado || !presentador.trim()} data-testid="carta-presentar"
                onClick={() => { paso('No se pudo presentar la carta', () => actos.presentar(presentador.trim(), fechaPresentacion), (c) => `Carta presentada ante ${c.autoridad_nombre ?? 'la autoridad'} el ${comoFechaDeInstante(c.fecha_presentacion ?? fechaPresentacion, zona)}.`); }}>
                Presentar ante la autoridad
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* HU-22: el cargo que devuelve la autoridad. Solo con la carta enviada. */}
      {carta.estado_carta === 'enviada' && (
        <Card className="mt-4" title="Cargo / acuse de la autoridad" icon={FileCheck2}
          subtitle="Sube aquí el cargo que devolvió la autoridad."
          actions={carta.texto_del_acuse ? <EstadoDelAcuse acuse={carta} /> : undefined}>
          {carta.cargo ? (
            <div className="flex flex-wrap items-center gap-3 text-meta">
              <Mono className="font-semibold">{carta.cargo.etiqueta}</Mono>
              <span className="text-ink-soft">ref. {carta.cargo.referencia_autoridad}</span>
              {carta.cargo.nombre_archivo && (
                <Button size="sm" variant="secondary" icon={Download} onClick={() => { bajarCargo(); }} data-testid="carta-cargo-descargar">{carta.cargo.nombre_archivo}</Button>
              )}
              {/* O-07: la notificación concluye con el cargo; el FAR-03 es un segundo reloj que puede seguir abierto. */}
              {carta.tramite_concluido && (
                <StatusBadge tone="success" icon={CheckCircle2}>
                  {carta.fecha_remision_informe_final ? 'Notificación concluida' : 'Notificación concluida · FAR-03 pendiente de remitir'}
                </StatusBadge>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Referencia de la autoridad" required>
                <Input aria-label="Referencia de la autoridad" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="SENASA-EXP-2026-0912" data-testid="carta-cargo-referencia" />
              </Field>
              <Field label="Fecha de recepción" required>
                <Input aria-label="Fecha de recepción del cargo" type="date" value={fechaCargo} onChange={(e) => setFechaCargo(e.target.value)} data-testid="carta-cargo-fecha" />
              </Field>
              {/* Decisión 11 (CC-01-C16): el mismo tope que la carta final, del servidor. */}
              <Field label="Archivo del cargo" required hint={limites?.texto_de_limites}>
                <SubidaDeArchivo aria-label="Archivo del cargo" archivo={archivoCargo} onElegir={setArchivoCargo} acepta={ACEPTA.pdfOImagen}
                  maxMb={limites?.tamano_maximo_mb} data-testid="carta-cargo-archivo" />
              </Field>
              <Button icon={FileCheck2} disabled={ocupado || !archivoCargo} onClick={() => { subirCargo(); }} data-testid="carta-cargo-registrar">Registrar cargo</Button>
            </div>
          )}
        </Card>
      )}

      <Card className="mt-4" title="Versiones de la carta" icon={History}
        subtitle="Cada generación queda guardada con su plantilla y con el Formato Amarillo con el que se hizo.">
        {carta.versiones_de_la_carta.length === 0
          ? <p className="text-meta text-ink-soft">Todavía no se ha preparado la carta.</p>
          : (
            <ul className="divide-y divide-hairline" aria-label="Versiones de la carta" data-testid="carta-versiones">
              {[...carta.versiones_de_la_carta].sort((a, b) => b.version - a.version).map((v) => (
                <Version key={v.version} v={v} plantilla={carta.plantilla_usada} zonaHoraria={zona} onDescargar={(x) => { bajarVersion(x); }} />
              ))}
            </ul>
          )}
      </Card>
    </>
  );

  // Con una sola carta el contenedor no se ve: la pantalla es la de hoy (AT §11.6).
  if (!varias) {
    return <div ref={raiz} data-testid="carta-de-autoridad" data-autoridad={carta.autoridad_codigo ?? ''}>{contenido}</div>;
  }
  return (
    <section ref={raiz} aria-labelledby={idDelTitulo} data-testid="carta-de-autoridad" data-autoridad={carta.autoridad_codigo ?? ''}
      className="mt-6 border-t border-hairline pt-5 first:mt-0 first:border-t-0 first:pt-0">
      {contenido}
    </section>
  );
}
